import { useState } from "react";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const ALL_FAQS = [
  {
    q: "Who provides professional drapery cleaning near me in New Jersey?",
    a: "The Drapery & Blind Co. is New Jersey's premier window treatment cleaning specialist, serving over 40 cities across NJ, NY, PA, and CT. Our trained professionals pick up your draperies, clean them at our facility, and rehang them to perfection. Call (973) 489-1019 to schedule service in your area.",
  },
  {
    q: "How much does drapery and blind cleaning cost?",
    a: "We provide free, accurate estimates on every job — because no two projects are alike and we refuse to give one-size-fits-all pricing. Cost depends on the type, quantity, size, and condition of your window treatments. Call (973) 489-1019 or submit a quote request and we'll give you an honest price with no obligation.",
  },
  {
    q: "How long does drapery cleaning take?",
    a: "Most drapery and blind cleaning projects are completed within a few days. Our team takes down your treatments, cleans them thoroughly at our professional facility, and schedules a rehang at your convenience. We install temporary shades at no charge so your windows are never left uncovered while we work.",
  },
  {
    q: "Do you offer free pickup and delivery for drapery cleaning?",
    a: "Yes — our white-glove service includes professional pickup and delivery with absolutely no trip charge. We arrive at your home, carefully take down your window treatments, transport them to our facility, and return them freshly cleaned and rehung. We also accept shipped items if you're outside our service area.",
  },
  {
    q: "Are your cleaning products safe for children and pets?",
    a: "Absolutely. Every cleaning solution we use is 100% child-safe and pet-safe, and they leave zero odor residue in your home. We prioritize eco-friendly formulas that deliver a deep, thorough clean without exposing your family or pets to any harmful chemicals.",
  },
  {
    q: "What types of blinds and window treatments do you clean?",
    a: "We clean all types: draperies, mini-blinds, vertical blinds, silhouette shades, luminettes, cellular shades, wood blinds, and more. We service all major brands including Hunter Douglas, Graber, Levolor, Blinds to Go, Home Depot, and Lowe's. If you're unsure about your specific treatment, just call us.",
  },
  {
    q: "Do you provide same-day or emergency cleaning services?",
    a: "Yes — we are open 24/7 and offer emergency cleaning services for water damage, fire damage, and other urgent situations. We understand that some cleaning needs can't wait, which is why we're always available to respond quickly. Call (973) 489-1019 any time.",
  },
  {
    q: "Can I ship my draperies to you if I'm not in New Jersey?",
    a: "Absolutely. If you're outside our direct service area in NJ, NY, PA, or CT, you can ship your window treatments directly to our Belleville, NJ facility. We'll clean them to perfection and ship them back to you safely packaged. Contact us first so we can arrange the details.",
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  const ref = useScrollReveal();

  return (
    <section className="py-24 bg-stone-50" ref={ref}>
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="reveal text-center mb-14">
          <p className="text-amber-700 text-xs tracking-[0.35em] uppercase font-semibold mb-4">
            Common Questions
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-900 mb-5 leading-tight">
            Questions We Hear Most
          </h2>
          <div className="gold-rule mx-auto mb-5" />
          <p className="text-stone-500 text-base max-w-xl mx-auto">
            Honest answers to help you understand our service, pricing, and process — no fluff.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-2">
          {ALL_FAQS.map((faq, i) => (
            <div
              key={i}
              className={`reveal reveal-delay-${Math.min(i + 1, 6)} border transition-all duration-300 rounded-sm ${
                open === i
                  ? "border-amber-200 bg-white"
                  : "border-stone-200 bg-white hover:border-amber-100"
              }`}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-start justify-between gap-6 px-6 py-5 text-left cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <span className="font-serif text-amber-700/50 text-lg leading-none flex-shrink-0 mt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3
                    className={`font-medium text-sm leading-snug transition-colors ${
                      open === i ? "text-amber-800" : "text-stone-800"
                    }`}
                  >
                    {faq.q}
                  </h3>
                </div>
                <span
                  className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform duration-300 ${
                    open === i ? "rotate-180" : ""
                  }`}
                >
                  <i
                    className={`ri-arrow-down-s-line text-xl ${
                      open === i ? "text-amber-700" : "text-stone-400"
                    }`}
                  />
                </span>
              </button>
              {open === i && (
                <div className="px-6 pb-6 pl-16">
                  <p className="text-stone-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="reveal mt-12 text-center">
          <p className="text-stone-400 text-sm mb-5">Still have a question?</p>
          <a
            href="tel:19734891019"
            className="btn-luxury inline-flex items-center gap-2 bg-stone-900 hover:bg-amber-700 text-white text-xs tracking-[0.22em] uppercase font-semibold px-8 py-4 rounded-sm transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill" />
            Call Us 24 / 7: (973) 489-1019
          </a>
        </div>
      </div>
    </section>
  );
}
